/**
 * @fileoverview Exports backoffice.
 *
 * Liste mockée des jobs asynchrones et formulaire de demande d'export.
 */

import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Button,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  SearchInput,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusBadge,
} from '@gnpc/ui';
import { FileDown, Play, Plus, Repeat, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { EXPORT_JOBS, formatDateTime, type ExportJob } from '../data/backoffice.ts';

/**
 * Modèles d'export prédéfinis pour le reporting QFS (C-27) — pré-remplissent
 * le périmètre et les filtres du formulaire de création.
 */
const EXPORT_TEMPLATES: Record<
  string,
  { label: string; scope: string; format: 'CSV' | 'XLSX' | 'PDF'; paymentStatus: string; sgiStatus: string }
> = {
  'anomalies-qfs': {
    label: 'Reporting QFS — anomalies d’enregistrement',
    scope: 'Paiements',
    format: 'XLSX',
    paymentStatus: 'PAID',
    sgiStatus: 'FAILED',
  },
  'rapprochement-quotidien': {
    label: 'Rapprochement quotidien',
    scope: 'Rapprochement',
    format: 'CSV',
    paymentStatus: 'ALL',
    sgiStatus: 'ALL',
  },
  'audit-mensuel': {
    label: 'Audit financier mensuel',
    scope: 'Audit',
    format: 'PDF',
    paymentStatus: 'ALL',
    sgiStatus: 'ALL',
  },
};

const columns: ColumnDef<ExportJob>[] = [
  { accessorKey: 'id', header: 'Export', cell: ({ row }) => <code>{row.original.id}</code> },
  { accessorKey: 'format', header: 'Format' },
  { accessorKey: 'scope', header: 'Périmètre' },
  {
    accessorKey: 'status',
    header: 'Statut',
    cell: ({ row }) => <StatusBadge kind="export" status={row.original.status} compact />,
  },
  { accessorKey: 'createdBy', header: 'Demandé par' },
  { accessorKey: 'createdAt', header: 'Créé le', cell: ({ row }) => formatDateTime(row.original.createdAt) },
];

function downloadExportJob(job: ExportJob): void {
  const content = [
    `Export;${job.id}`,
    `Format;${job.format}`,
    `Périmètre;${job.scope}`,
    `Demandé par;${job.createdBy}`,
    `Créé le;${formatDateTime(job.createdAt)}`,
    job.expiresAt ? `Expire le;${formatDateTime(job.expiresAt)}` : '',
  ]
    .filter(Boolean)
    .join('\n');
  const blob = new Blob(['\uFEFF', content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${job.id}.${job.format.toLowerCase()}`;
  link.click();
  URL.revokeObjectURL(url);
}

export function ExportsPage(): JSX.Element {
  const [query, setQuery] = useState('');
  const [jobStatus, setJobStatus] = useState('ALL');
  const [selectedJobId, setSelectedJobId] = useState(EXPORT_JOBS[0]?.id ?? '');
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [format, setFormat] = useState<'CSV' | 'XLSX' | 'PDF'>('XLSX');
  const [scope, setScope] = useState('Paiements');
  const [paymentStatus, setPaymentStatus] = useState('PAID');
  const [sgiStatus, setSgiStatus] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('2026-07-01');
  const [dateTo, setDateTo] = useState('2026-07-14');
  /** Modèle prédéfini appliqué (C-27). */
  const [template, setTemplate] = useState('');
  /** Récurrence de l'export planifié (C-25). */
  const [recurrence, setRecurrence] = useState('NONE');
  /** Destinataire email d'un export planifié (C-25). */
  const [emailTo, setEmailTo] = useState('');

  /** Applique un modèle prédéfini : pré-remplit périmètre, format et filtres. */
  function applyTemplate(key: string): void {
    setTemplate(key);
    const preset = EXPORT_TEMPLATES[key];
    if (!preset) return;
    setScope(preset.scope);
    setFormat(preset.format);
    setPaymentStatus(preset.paymentStatus);
    setSgiStatus(preset.sgiStatus);
  }

  const filteredJobs = useMemo(() => {
    return EXPORT_JOBS.filter((job) => {
      const matchesQuery =
        query.length === 0 ||
        job.id.toLowerCase().includes(query.toLowerCase()) ||
        job.scope.toLowerCase().includes(query.toLowerCase()) ||
        job.createdBy.toLowerCase().includes(query.toLowerCase());
      const matchesStatus = jobStatus === 'ALL' || job.status === jobStatus;
      return matchesQuery && matchesStatus;
    });
  }, [jobStatus, query]);

  const latestCompletedExport = EXPORT_JOBS.find((job) => job.status === 'COMPLETED');
  const selectedJob =
    EXPORT_JOBS.find((job) => job.id === selectedJobId) ?? latestCompletedExport ?? EXPORT_JOBS[0];

  const criteria = [
    scope,
    `${dateFrom} → ${dateTo}`,
    `Paiement ${paymentStatus}`,
    sgiStatus === 'ALL' ? 'Tous (enreg. Quipux)' : `Quipux ${sgiStatus}`,
    format,
  ];

  return (
    <>
      <Sheet>
        <div className="grid gap-6">
          <PageHeader
            eyebrow="Exports"
            title="Créer et suivre les exports"
            description="Préparer, générer et suivre les exports CSV, XLSX ou PDF."
            actions={
              <>
                <Button
                  variant="outline"
                  disabled={!latestCompletedExport}
                  onClick={() => {
                    if (latestCompletedExport) downloadExportJob(latestCompletedExport);
                  }}
                >
                  <FileDown className="size-4" />
                  Télécharger le dernier export
                </Button>
                <SheetTrigger asChild>
                  <Button>
                    <Plus className="size-4" />
                    Nouvel export
                  </Button>
                </SheetTrigger>
              </>
            }
          />

          <DataTable
            columns={columns}
            data={filteredJobs}
            toolbar={
              <div className="grid w-full gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
                <SearchInput
                  size="sm"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onClear={() => setQuery('')}
                  placeholder="Export, périmètre, demandeur…"
                />
                <select
                  value={jobStatus}
                  onChange={(event) => setJobStatus(event.target.value)}
                  className="border-input bg-background h-9 rounded-md border px-3 text-sm"
                  aria-label="Statut de l'export"
                >
                  <option value="ALL">Tous statuts</option>
                  <option value="PENDING">En préparation</option>
                  <option value="COMPLETED">Terminé</option>
                  <option value="FAILED">Échoué</option>
                  <option value="EXPIRED">Expiré</option>
                </select>
              </div>
            }
            mobileCardLabel="Exports"
            mobileCard={(job) => (
              <DataCard
                eyebrow={job.id}
                title={job.scope}
                description={`Demandé par ${job.createdBy}`}
                aside={<StatusBadge kind="export" status={job.status} compact />}
                meta={
                  <>
                    <span className="text-muted-foreground">{job.format}</span>
                    <span className="text-muted-foreground">{formatDateTime(job.createdAt)}</span>
                    {job.expiresAt && (
                      <span className="text-muted-foreground">
                        Expire {formatDateTime(job.expiresAt)}
                      </span>
                    )}
                  </>
                }
              />
            )}
            rowAction={{
              label: (job) => `Consulter l'export ${job.id}`,
              onSelect: (job) => {
                setSelectedJobId(job.id);
                setDetailSheetOpen(true);
              },
            }}
            emptyState={<EmptyState title="Aucun export" description="Les exports créés apparaîtront ici." />}
            pagination={{ label: `${filteredJobs.length} export(s)`, hasNextPage: false }}
          />
        </div>

        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Nouvel export</SheetTitle>
            <SheetDescription>Filtrer précisément le périmètre à générer.</SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 p-5">
            {/* Modèles prédéfinis pour le reporting QFS (C-27). */}
            <FormField label="Modèle prédéfini">
              <select
                value={template}
                onChange={(event) => applyTemplate(event.target.value)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="">Aucun (configuration manuelle)</option>
                {Object.entries(EXPORT_TEMPLATES).map(([key, preset]) => (
                  <option key={key} value={key}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Périmètre" required>
              <select
                value={scope}
                onChange={(event) => setScope(event.target.value)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="Paiements">Paiements</option>
                <option value="Remboursements">Remboursements</option>
                <option value="Audit">Audit</option>
                <option value="Rapprochement">Rapprochement</option>
              </select>
            </FormField>
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Du" required>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </FormField>
              <FormField label="Au" required>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </FormField>
            </div>
            <FormField label="Statut paiement" required>
              <select
                value={paymentStatus}
                onChange={(event) => setPaymentStatus(event.target.value)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="ALL">Tous</option>
                <option value="PAID">Payé</option>
                <option value="PENDING">En attente</option>
                <option value="FAILED">Échoué</option>
              </select>
            </FormField>
            <FormField label="Enregistrement Quipux">
              <select
                value={sgiStatus}
                onChange={(event) => setSgiStatus(event.target.value)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="ALL">Tous</option>
                <option value="RECORDED">Enregistré</option>
                <option value="PENDING">En attente</option>
                <option value="FAILED">Échec</option>
                <option value="PARTIAL">Partiel</option>
              </select>
            </FormField>
            <div className="grid grid-cols-3 gap-2">
              {(['CSV', 'XLSX', 'PDF'] as const).map((item) => (
                <Button
                  key={item}
                  type="button"
                  variant={format === item ? 'default' : 'outline'}
                  onClick={() => setFormat(item)}
                >
                  {item}
                </Button>
              ))}
            </div>
            {/* Planification récurrente + envoi par email (C-25). */}
            <FormField label="Récurrence">
              <select
                value={recurrence}
                onChange={(event) => setRecurrence(event.target.value)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="NONE">Ponctuel</option>
                <option value="DAILY">Quotidien</option>
                <option value="WEEKLY">Hebdomadaire</option>
                <option value="MONTHLY">Mensuel</option>
              </select>
            </FormField>
            {recurrence !== 'NONE' ? (
              <FormField label="Envoyer par email à">
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(event) => setEmailTo(event.target.value)}
                  placeholder="agent@quipux.ci"
                />
              </FormField>
            ) : null}
            <p className="text-muted-foreground text-xs">
              Les exports sont conservés 7 jours et tracés dans le journal d'audit.
            </p>
            <div className="flex flex-wrap gap-2">
              {criteria.map((item) => (
                <span
                  key={item}
                  className="bg-quipux-navy-50 text-quipux-navy-900 rounded-full px-3 py-1 text-xs"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <SheetFooter className="border-border border-t p-5">
            <SheetClose asChild>
              <Button
                onClick={() =>
                  toast.success(
                    recurrence === 'NONE' ? "Export en file d'attente" : 'Export récurrent planifié',
                    {
                      description:
                        recurrence === 'NONE'
                          ? `${scope} · ${dateFrom} → ${dateTo} · format ${format} — disponible dans la liste une fois généré.`
                          : `${scope} · ${format} · ${
                              recurrence === 'DAILY'
                                ? 'chaque jour'
                                : recurrence === 'WEEKLY'
                                  ? 'chaque semaine'
                                  : 'chaque mois'
                            }${emailTo ? ` — envoyé à ${emailTo}` : ''}.`,
                    },
                  )
                }
              >
                <Play className="size-4" />
                {recurrence === 'NONE' ? "Lancer l'export" : "Planifier l'export"}
              </Button>
            </SheetClose>
            <SheetClose asChild>
              <Button variant="outline">Annuler</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Détail de l'export</SheetTitle>
            <SheetDescription>{selectedJob?.id ?? 'Export'} · {selectedJob?.format}</SheetDescription>
          </SheetHeader>

          {selectedJob ? (
            <div className="grid gap-4 p-5">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase">Périmètre</p>
                    <p className="text-quipux-navy-950 mt-1 font-medium">{selectedJob.scope}</p>
                  </div>
                  <StatusBadge kind="export" status={selectedJob.status} compact />
                </div>
              </div>

              <dl className="grid gap-3 rounded-lg border border-border p-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Demandé par</dt>
                  <dd className="mt-1 font-medium">{selectedJob.createdBy}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-muted-foreground">Format</dt>
                    <dd className="mt-1 font-medium">{selectedJob.format}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Créé le</dt>
                    <dd className="mt-1">{formatDateTime(selectedJob.createdAt)}</dd>
                  </div>
                </div>
                {selectedJob.expiresAt ? (
                  <div>
                    <dt className="text-muted-foreground">Disponible jusqu'au</dt>
                    <dd className="mt-1">{formatDateTime(selectedJob.expiresAt)}</dd>
                  </div>
                ) : null}
                {selectedJob.recurrence ? (
                  <div>
                    <dt className="text-muted-foreground">Récurrence</dt>
                    <dd className="mt-1 flex items-center gap-1.5 font-medium">
                      <Repeat className="size-3.5" />
                      {selectedJob.recurrence}
                    </dd>
                  </div>
                ) : null}
              </dl>

              {selectedJob.status === 'COMPLETED' ? (
                <div className="rounded-lg border border-status-paid/25 bg-status-paid/5 p-4 text-sm">
                  <p className="text-quipux-navy-950 font-medium">Export disponible</p>
                  <p className="text-muted-foreground mt-1">
                    Le fichier est prêt et peut être téléchargé.
                  </p>
                </div>
              ) : selectedJob.status === 'FAILED' ? (
                <div className="border-danger/25 bg-danger/5 rounded-lg border p-4 text-sm">
                  <p className="text-quipux-navy-950 font-medium">Export en échec</p>
                  {selectedJob.failureReason ? (
                    <p className="text-muted-foreground mt-1">{selectedJob.failureReason}</p>
                  ) : null}
                  {/* Relance d'un job échoué (C-26). */}
                  <Button
                    size="sm"
                    className="mt-3"
                    onClick={() =>
                      toast.success('Export relancé', {
                        description: `${selectedJob.id} — nouvelle tentative de génération lancée.`,
                      })
                    }
                  >
                    <RotateCcw className="size-4" />
                    Relancer l'export
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border border-border p-4 text-sm">
                  <p className="text-quipux-navy-950 font-medium">Export en préparation</p>
                  <p className="text-muted-foreground mt-1">
                    Le fichier sera téléchargeable une fois terminé.
                  </p>
                </div>
              )}
            </div>
          ) : null}

          <SheetFooter className="border-border border-t p-5">
            <Button
              disabled={!selectedJob || selectedJob.status !== 'COMPLETED'}
              onClick={() => {
                if (selectedJob) downloadExportJob(selectedJob);
              }}
            >
              <FileDown className="size-4" />
              Télécharger l'export
            </Button>
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
